/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useState } from 'react';
import merge from 'lodash.merge';
import { parse as parseUrl } from 'url';
import { useRouter } from 'next/router';
import getConfig from 'next/config';
import { removePathTrailingSlash } from '../ports/next';
import App, { AppProps, AppContext, NextWebVitalsMetric } from 'next/app';
import { CacheProvider, Global, css } from '@emotion/react';
import { cache } from '@emotion/css';
import { ThemeProvider, reset, theme as defaultTheme } from '@teko/ui';
import {
  generateTheme,
  ThemeProvider as DSThemeProvider,
} from '@teko/ds-desktop';
import { MenuItem } from '@teko/discovery';
import NextTracker from 'next-tracker';
import { globalStyles } from '../globalStyles';
import {
  HistoryProvider,
  CartProvider,
  GlobalDataProvider,
  useGlobalData,
  ToastProvider,
  FeatureFlagProvider,
  PopularKeywordProvider,
  ServiceProvider,
  initializeServices,
  ScreenProvider,
  FrameProvider,
  HelperProvider,
  NotificationProvider,
} from '../contexts';
import { AuthContextProvider, useAuth } from '@teko/auth-react';
import { saveUtmCookie } from '../helpers/tracking';
import routesTracking from './_routes';
import { BannerEntity, Area } from '../types';
import { TenantEnv } from '../types';
import layouts from './_layouts';
import { isHomepage, getUserSelectedInfo } from 'helpers';
import { setCookieByName } from 'helpers';
import { FlagState } from '../libs';
import { useRouterEffect, useI18n, usePos365StoreCode } from 'hooks';
import { userSelectedConstants } from '../constants';
import MetaHead from 'components/mobile/organisms/MetaHead';
import { changeLanguage, DEFAULT_LANGUAGE } from 'i18n';
import { translateComplexObject } from 'tenants/_template/desktop/_helpers/translate';
import { getRightMenu } from 'tenants/_template/desktop/_helpers';
import { LanguageSelector } from 'components/desktop/organisms';
import { logger } from '@teko/log';
import { DeviceProvider } from '@teko/teko-ecommerce';

const serverless = typeof window !== 'undefined';

export interface MyAppProps extends AppProps {
  globalData: TenantEnv;
  banners: BannerEntity[];
  featureFlags: FlagState;
  menuItems?: MenuItem[];
  liteLogo: boolean;
  layoutAreas?: Area[];
}

interface LayoutProps {
  children: React.ReactNode;
  banners?: BannerEntity[];
  menuItems?: MenuItem[];
  liteLogo: boolean;
  layoutAreas?: Area[];
}

const Layout: React.FC<LayoutProps> = ({
  children,
  banners,
  menuItems,
  liteLogo,
  layoutAreas,
}: LayoutProps) => {
  const {
    isMobile,
    dealerRequestRequireApproval,
    template,
    tenant,
    footerDataConfig = {},
    rightMenuTopBarButtonConfigs = [],
    availableLanguages,
    identityRequired,
  } = useGlobalData();
  const { user } = useAuth();
  const { currentLanguage } = useGlobalData();

  // load a language resource on the client
  useI18n(currentLanguage || DEFAULT_LANGUAGE);

  useEffect(() => {
    if (serverless && window.track) {
      const userId = user?.userId || window.TekoID?.user?.getUserInfo()?.sub;
      if (userId) {
        window.track('setUserId', userId);
      }
    }
  }, [user?.userId, serverless && !!window.TekoID?.user?.getUserInfo()]);

  //@TODO: remove tenant in here
  const tpl = template || tenant;
  const defaultTemplate = 'vnshop';
  const footerData = translateComplexObject(footerDataConfig);
  const rightMenu = getRightMenu(
    translateComplexObject(rightMenuTopBarButtonConfigs),
    !!availableLanguages ? [LanguageSelector] : [],
  );

  if (isMobile) {
    const MobileLayout =
      layouts.get(`${tpl}_mobile`) || layouts.get(`${defaultTemplate}_mobile`);
    // using dealerRequestRequireApproval flag to
    // force login for all pages
    // (not quite what this flag supposed to do)
    return (
      <MobileLayout
        forceLogin={
          dealerRequestRequireApproval ||
          identityRequired === 'dealer-not-required'
        }
        layoutAreas={layoutAreas}
      >
        {children}
      </MobileLayout>
    );
  } else {
    const DesktopLayout =
      layouts.get(`${tpl}_desktop`) ||
      layouts.get(`${defaultTemplate}_desktop`);
    return (
      <DesktopLayout
        banners={banners}
        menuItems={menuItems}
        liteLogo={liteLogo}
        footerData={footerData}
        rightMenu={rightMenu}
        // hotfix the forceLogin for karavan
        // will refractor this in ECOM-362
        forceLogin={identityRequired === 'dealer-not-required'}
        layoutAreas={layoutAreas}
      >
        {children}
      </DesktopLayout>
    );
  }
};

// linter replaces "interface" with "type"
// which causes compile error
// so disable lint for next line
// eslint-disable-next-line
interface ClientTenantEnvImpl extends TenantEnv { }
class ClientTenantEnvImpl {
  constructor(config: TenantEnv) {
    // Get terminal from cookie in case tenant has terminal by region (ex: karavan, umbala)
    // This help avoid disk cache from browser when change location
    // More detail: https://jira.teko.vn/browse/UX4BUYER-2566
    if (Array.isArray(config.originalTerminal)) {
      config.terminal =
        getUserSelectedInfo(userSelectedConstants.TERMINAL, config.isMobile) ||
        config.terminal;

      config.terminalId =
        parseInt(
          getUserSelectedInfo(userSelectedConstants.TERMINAL_ID, this.isMobile),
        ) || config.terminalId;

      config.location =
        getUserSelectedInfo(userSelectedConstants.LOCATION, config.isMobile) ||
        config.location;
    }

    Object.assign(this, config);

    // assign the object to window
    // this a trick to persist the
    // config object for client-side only pages
    (window as any).__tenantConfig = config;
  }

  getProcessEnv(env: string): string {
    return window?.env?.[env];
  }
}

const MyApp: React.FunctionComponent<MyAppProps> = ({
  Component,
  pageProps,
  globalData,
  banners,
  menuItems,
  featureFlags,
  liteLogo,
  layoutAreas,
}: MyAppProps) => {
  if (!globalData) {
    logger.error(
      `Empty global data when render ${Component.name}. Probably unable to detect tenant from hostname.`,
    );
    throw new Error('Missing global data when rendering component');
  }

  // menuItems, banners and layoutAreas (custom page)
  // are fetched in Document.getInitialProps
  // and passed to MyApp in renderPage function.
  // For client-side transition,
  // get them from window object instead.
  banners = banners || (serverless ? (window as any).banners : []);
  menuItems = menuItems || (serverless ? (window as any).menuItems : []);
  layoutAreas = layoutAreas || (serverless ? (window as any).layoutAreas : []);

  const { publicRuntimeConfig } = getConfig();

  const { tenant, isMobile, scopes } = globalData;

  // object fetched by getInitialProps does not
  // preserve fucntion getProcessEnv when comes to client-side
  // re-initialize the object for that
  if (!globalData.getProcessEnv)
    globalData = new ClientTenantEnvImpl(globalData);

  if (!serverless) {
    changeLanguage(globalData.currentLanguage || DEFAULT_LANGUAGE);
  }

  const router = useRouter();
  const { query } = router;

  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (history[history.length - 1] !== router.asPath) {
      setHistory((prevState) => [...prevState, router.asPath]);
    }
  }, [router]);

  if (router && !(router as any).intercepted) {
    const customizedRouter = router as any;
    // the singleton router object created
    // intercept call to this method

    // this code ensure that when a next/link is clicked on the client-side
    // and navigation happens, the actual data fetched is prefixed with <tenant> path
    // Under the hood, next/link calls next/router to trigger page navigation
    // and it populates "as" field by default with the same value as "path";
    ['replace', 'push', 'prefetch'].forEach((method) => {
      const originalMethod = customizedRouter[method];
      customizedRouter[method] = (path: any, asPath: any, ...args: any) => {
        const { buildId } = window.__NEXT_DATA__;
        const defaultTenant = publicRuntimeConfig.defaultTenantDir;
        const { pathname } = parseUrl(
          typeof path === 'string' ? path : path.pathname,
        );
        if (pathname.match(/\/([\w\d\-]+)\/(mobile|desktop)(.*)/)) {
          return originalMethod.call(router, path, asPath, ...args);
        }
        const platform = isMobile ? 'mobile' : 'desktop';
        const pages = window.__BUILD_MANIFEST?.sortedPages || [];

        const customizedPath = removePathTrailingSlash(
          `/${tenant}/${platform}${pathname}`,
        );
        const defaultPath = removePathTrailingSlash(
          `/${defaultTenant}/${platform}${pathname}`,
        );
        let finalTenant = '';

        if (buildId === 'development') {
          finalTenant = pages.includes(customizedPath)
            ? tenant
            : pages.includes(defaultPath)
            ? defaultTenant
            : tenant;
        } else {
          finalTenant = pages.includes(customizedPath) ? tenant : defaultTenant;
        }

        const param =
          typeof path === 'string'
            ? removePathTrailingSlash(`/${finalTenant}/${platform}${path}`)
            : {
                ...path,
                pathname: removePathTrailingSlash(
                  `/${finalTenant}/${platform}${path.pathname}`,
                ),
              };

        asPath = asPath || path;
        return originalMethod.call(router, param, asPath, ...args);
      };
    });

    const originalMethod = customizedRouter['back'];
    customizedRouter['back'] = (...args: any) => {
      if (!history.length) return router.push('/');
      return originalMethod.call(...args);
    };

    (router as any).intercepted = true;
  }

  useRouterEffect(router, globalData);
  usePos365StoreCode();

  useEffect(() => {
    const trackerAppId = globalData.getProcessEnv('TRACKER_APP_ID');
    if (trackerAppId) {
      new NextTracker({
        host: globalData.getProcessEnv('TRACKER_HOST_URL'),
        urlServeJsFile: globalData.getProcessEnv('TRACKER_SERVER_JS_URL'),
        appId: trackerAppId,
        routes: routesTracking as any,
        injectScript: false,
      });
      window.track('enableTrackVisibleContentImpressions');

      if (serverless) {
        const originalFetch = window.fetch;
        window.fetch = function (...args) {
          return originalFetch
            .apply(this, args)
            .then(async function (data: any) {
              if (data.status >= 400) {
                data
                  .clone()
                  .json()
                  .then((res: any) => {
                    window.track('error', {
                      errorSource: 'http',
                      apiCall: data.url,
                      apiPayload: JSON.stringify(args),
                      httpResponseCode: `${data.status}`,
                      responseJson: JSON.stringify(res),
                      errorCode: `${res.code}` || 'unknown',
                      errorMessage: res.message || 'unknown',
                    });
                  });
              }
              return data;
            });
        };
      }
    } else {
      if (serverless) {
        // polyfill with a dummy tracking function
        // to avoid undefined fn errors somewhere else
        window.track = () => {};
      }
    }

    saveUtmCookie(query);
    if (query?.referral) {
      setCookieByName(
        'referral',
        `${query.referral.toString()};SameSite=None;Secure;`,
      );
    }
  }, []);

  const services = initializeServices(globalData);
  const uiTheme = merge({}, defaultTheme, globalData.theme);
  const dsTheme = generateTheme({
    primary: uiTheme.brand.primary,
    secondary: uiTheme.brand.secondary,
  });
  dsTheme.colors = { ...dsTheme.colors, ...uiTheme.colors };

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={uiTheme}>
        <DSThemeProvider theme={dsTheme}>
          <MetaHead
            theme={uiTheme}
            config={globalData}
            meta={globalData.meta}
          />
          <GlobalDataProvider value={globalData}>
            <ServiceProvider services={services}>
              <HelperProvider
                discoveryPos365Service={services.discoveryPos365Service}
                freeShippingThreshold={globalData?.freeShippingThreshold}
              >
                <AuthContextProvider
                  partner={globalData.partner}
                  scopes={scopes || ['openid', 'profile', 'us', 'om', 'ppm']}
                  userService={services.userService}
                  authService={services.authService}
                  router={router}
                >
                  <FeatureFlagProvider
                    flags={
                      featureFlags || (serverless ? window.featureFlags : null)
                    }
                  >
                    <FrameProvider enabled>
                      <PopularKeywordProvider>
                        <ToastProvider>
                          <ScreenProvider>
                            <CartProvider>
                              <NotificationProvider>
                                <Global styles={css(reset, globalStyles)} />
                                <DeviceProvider value={{ isMobile }}>
                                  <Layout
                                    banners={banners}
                                    menuItems={menuItems}
                                    liteLogo={liteLogo}
                                    layoutAreas={layoutAreas}
                                  >
                                    <HistoryProvider>
                                      <Component
                                        {...pageProps}
                                        menuItems={menuItems}
                                      />
                                    </HistoryProvider>
                                  </Layout>
                                </DeviceProvider>
                              </NotificationProvider>
                            </CartProvider>
                          </ScreenProvider>
                        </ToastProvider>
                      </PopularKeywordProvider>
                    </FrameProvider>
                  </FeatureFlagProvider>
                </AuthContextProvider>
              </HelperProvider>
            </ServiceProvider>
          </GlobalDataProvider>
        </DSThemeProvider>
      </ThemeProvider>
    </CacheProvider>
  );
};

(MyApp as any).getInitialProps = async (
  appContext: AppContext,
): Promise<any> => {
  // note this function is called every time
  // page transition happen,
  // so please think twice before adding any data-fetching logic
  // here.
  // Ideally, we would like to remove this function altogether.
  // But Nextjs's static optimization does not call
  // Document.getInitialProps for pages like /404,
  // which prevents the page from getting platform config.
  let globalTenantEnv;
  if (appContext.ctx.req) {
    // for pages that are SSR
    // this function is triggered on server-side
    globalTenantEnv = (appContext.ctx.req as any).tenantEnv;
  } else {
    // for client-side only page, get this value from window
    // since req object is not set
    globalTenantEnv = (window as any).__tenantConfig;
  }

  const liteLogo = !isHomepage(appContext.ctx.asPath);
  const appProps = await App.getInitialProps(appContext);
  return {
    ...appProps,
    globalData: globalTenantEnv,
    liteLogo,
  };
};

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (!serverless) return;

  if (metric.label === 'web-vital') {
    window.track?.('customEvent', 'web-vital', {
      category: metric.name,
      label: metric.label,
      value: Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value, // values must be integers
      ),
    });
  }
}

export default MyApp;
